import { useQuery } from '@tanstack/react-query';
import { pb } from '@/core/api';
import type { Creator } from '@/types';
import { countLeadsToday, filterByPeriod } from '@/utils/period-stats';

export type PersonalStats = {
  total: number;
  today: number;
  week: number;
  month: number;
  leads: Creator[];
};

async function fetchAllForUser(id: string): Promise<Creator[]> {
  const all: Creator[] = [];
  let page = 1;
  let hasMore = true;
  while (hasMore) {
    const batch = await pb.collection('leads').getList<Creator>(page, 200, {
      filter: `collector = "${id}" && is_void != true`,
      sort: '-created',
    });
    all.push(...batch.items);
    hasMore = batch.totalPages > page;
    page++;
  }
  return all;
}

function buildStats(all: Creator[]): PersonalStats {
  return {
    total: all.length,
    today: countLeadsToday(all),
    week: filterByPeriod(all, 'week').length,
    month: filterByPeriod(all, 'month').length,
    leads: all,
  };
}

// Single source of truth for per-user lead counts. Both `/earnings`
// (current user) and `/progress/:collectorId` (admin viewing another
// user) compute total/today/week/month off the same `leads` collection
// query so the four cards never disagree.
export function usePersonalStats(userId: string | undefined) {
  return useQuery({
    queryKey: ['personal-stats', userId],
    queryFn: async (): Promise<PersonalStats> =>
      buildStats(await fetchAllForUser(userId!)),
    enabled: !!userId,
    staleTime: 60_000,
  });
}
